import React, { useCallback } from "react";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import CsvImportDialog from "../../components/csv-import-dialog/CsvImportDialog";
import {
  invitationNotice,
  importButtonLabel,
  successMessage,
  barcodeNote,
} from "./importStylistsHelpers";

const REQUIRED_COLUMNS = ["first_name", "last_name", "email", "location"];

const FORMAT_GUIDE = [
  "Importing sends each stylist an invitation email with a temporary password, straight away",
  "email must be unique. A stylist whose email already exists is skipped",
  "location: the name of one of your salon locations. additional_locations: more location names separated by semicolons",
  "generate_barcode: 1 to give the stylist a barcode for barcode login, 0 or blank for none. Barcodes are generated for you and printed from the stylist's edit page",
  "phone: any format (digits are kept)",
  "Up to 100 stylists per file",
];

const PREVIEW_COLUMNS = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
  {
    key: "location",
    label: "Locations",
    render: (row) =>
      [row.location, ...(row.additional_locations || [])].join(", "),
  },
  {
    key: "generate_barcode",
    label: "Barcode",
    render: (row) => (row.generate_barcode ? "Yes" : "—"),
  },
];

const EXTRA_RESULT_COUNTS = [
  { key: "failed", label: "Failed" },
  { key: "not_processed", label: "Not processed" },
];

/** Rows that were valid but could not be created, with the reason for each. */
function FailedRows({ rows }) {
  if (!rows?.length) return null;
  return (
    <Alert severity="error" variant="outlined" sx={{ mb: 2, textAlign: "left" }}>
      <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        {rows.length === 1
          ? "1 stylist could not be created"
          : `${rows.length} stylists could not be created`}
      </AlertTitle>
      {rows.map((failed) => (
        <Typography key={failed.row} variant="body2">
          Row {failed.row}: {failed.email} {"—"} {failed.reason}
        </Typography>
      ))}
    </Alert>
  );
}

/** Rows the import did not reach before its time limit. */
function NotProcessedRows({ rows }) {
  if (!rows?.length) return null;
  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{ mb: 2, textAlign: "left" }}
    >
      <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        {rows.length === 1
          ? "1 stylist was not processed"
          : `${rows.length} stylists were not processed`}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        The import ran out of time. Upload the same file again: stylists
        already created are skipped and the rest are imported.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {rows.map((r) => r.email).join(", ")}
      </Typography>
    </Alert>
  );
}

/** Stylists CSV import. Flow and layout live in the shared CsvImportDialog. */
export default function ImportStylistsDialog({
  open,
  onClose,
  onImportComplete,
}) {
  const renderPreviewExtras = useCallback(({ dryRunResult }) => {
    const notice = invitationNotice(dryRunResult?.valid_rows);
    if (!notice) return null;
    return (
      <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
        {notice}
      </Alert>
    );
  }, []);

  const renderResultsExtras = useCallback(({ importResult }) => {
    const note = barcodeNote(importResult);
    return (
      <Box>
        <FailedRows rows={importResult.failed_rows} />
        <NotProcessedRows rows={importResult.not_processed_rows} />
        {note && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            {note}
          </Alert>
        )}
      </Box>
    );
  }, []);

  return (
    <CsvImportDialog
      open={open}
      onClose={onClose}
      onImportComplete={onImportComplete}
      entityPlural="stylists"
      entityTitle="Stylists"
      subtitle="Upload a CSV to add stylists in bulk. Each one is emailed an invitation."
      importPath="/users/import"
      templateFilename="stylist-import-template.csv"
      requiredColumns={REQUIRED_COLUMNS}
      optionalColumnsHint="Optional: phone, additional_locations, generate_barcode, stylist_meevo_id. Column names are case-insensitive."
      formatGuide={FORMAT_GUIDE}
      previewColumns={PREVIEW_COLUMNS}
      existingIdKey="existing_user_id"
      extraResultCounts={EXTRA_RESULT_COUNTS}
      renderPreviewExtras={renderPreviewExtras}
      renderResultsExtras={renderResultsExtras}
      importButtonLabel={importButtonLabel}
      resultsSuccessMessage={successMessage}
    />
  );
}
